import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Helmet } from 'react-helmet';
import { Lightbulb, Bug, Sparkles, MessageSquareMore, Monitor, Zap, Wrench, TabletSmartphone, Search, CircleUser, Squirrel, Circle, AlertCircle, AlertTriangle, Waypoints, X } from 'lucide-react';
import useThemeStore from '../store/themeStore';
import useTokenStore from '../store/store';
import { baseUrl } from '../utils';
import backgroundPattern from '../images/vesta/vesta-property-pattern-01-teal-bg.svg';

interface FeedbackForm {
  type: 'suggestion' | 'bug-report' | 'feature-request' | 'general';
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high';
  category: string;
  steps?: string;
  expected?: string;
  actual?: string;
  attachments?: File[];
}

const Feedback: React.FC = () => {
  const getThemes = useThemeStore((state) => state.theme);
  const getToken = useTokenStore((state) => state.token);
  const [formData, setFormData] = useState<FeedbackForm>({
    type: 'suggestion',
    title: '',
    description: '',
    priority: 'medium',
    category: '',
    steps: '',
    expected: '',
    actual: '',
    attachments: []
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'suggestion' | 'bug-report' | 'feature-request' | 'general'>('suggestion');
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isPriorityOpen, setIsPriorityOpen] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const priorityDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setIsCategoryOpen(false);
      }
      if (priorityDropdownRef.current && !priorityDropdownRef.current.contains(event.target as Node)) {
        setIsPriorityOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (field: keyof FeedbackForm, value: string | File[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTabChange = (tab: 'suggestion' | 'bug-report' | 'feature-request' | 'general') => {
    setActiveTab(tab);
    setFormData(prev => ({
      ...prev,
      type: tab
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const url = `${baseUrl}/whitelabel/feedback`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': getToken ? (getToken as string).replace('B9I9N9456', '') : '',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error:', errorText);
        throw new Error(`Failed to submit feedback: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Feedback submitted successfully:', result);
      
      setIsSubmitting(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('❌ Error submitting feedback:', error);
      setIsSubmitting(false);
      // You could add error state handling here if needed
      alert('Failed to submit feedback. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'suggestion',
      title: '',
      description: '',
      priority: 'medium',
      category: '',
      steps: '',
      expected: '',
      actual: '',
      attachments: []
    });
    setShowSuccessModal(false);
  };

  const closeModal = () => {
    setShowSuccessModal(false);
    // Clear the form when closing modal
    setFormData({
      type: 'suggestion',
      title: '',
      description: '',
      priority: 'medium',
      category: '',
      steps: '',
      expected: '',
      actual: '',
      attachments: []
    });
  };

  const tabs = [
    { id: 'suggestion', label: 'Suggestion', icon: Lightbulb },
    { id: 'bug-report', label: 'Bug Report', icon: Bug },
    { id: 'feature-request', label: 'Feature Request', icon: Sparkles },
    { id: 'general', label: 'General Feedback', icon: MessageSquareMore }
  ] as const;

  const categories = [
    { value: 'ui-ux', label: 'User Interface & Experience', icon: Monitor },
    { value: 'performance', label: 'Performance & Speed', icon: Zap },
    { value: 'functionality', label: 'Functionality & Features', icon: Wrench },
    { value: 'mobile', label: 'Mobile Experience', icon: TabletSmartphone },
    { value: 'search', label: 'Search & Filters', icon: Search },
    { value: 'account', label: 'Account & Profile', icon: CircleUser },
    { value: 'other', label: 'Something Else', icon: Squirrel }
  ] as const;

  const priorities = [
    { value: 'low', label: 'Low - Nice to have', icon: Circle, color: '#10B981' },
    { value: 'medium', label: 'Medium - Important', icon: AlertCircle, color: '#F59E0B' },
    { value: 'high', label: 'High - Critical', icon: AlertTriangle, color: '#EF4444' }
  ] as const;


  return (
    <>
    <div className="min-h-screen flex flex-col pt-28">
      <Helmet>
        <title>Feedback - Help Us Improve</title>
        <meta name="description" content="Share your suggestions, report bugs, or request new features to help us improve your experience." />
      </Helmet>

      {/* Background Pattern */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-5"
        style={{ backgroundImage: `url(${backgroundPattern})` }}
      />

      {/* Main Content */}
      <div className="relative z-10 flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4" style={{ color: getThemes?.txtColor }}>
              Share Your Feedback
            </h1>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#000000' }}>
              Help us improve by sharing your suggestions, reporting bugs, or requesting new features. 
              Your input is invaluable to making our platform better for everyone.
            </p>
          </div>

          {/* Feedback Type Tabs */}
          <div className="mb-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`p-4 rounded-xl transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'shadow-lg scale-105'
                      : 'hover:shadow-md hover:scale-102'
                  }`}
                  style={{
                    backgroundColor: activeTab === tab.id 
                      ? getThemes?.color2 || '#E9B54E'
                      : getThemes?.color1 || '#1A2347'
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.backgroundColor = getThemes?.color2 || '#E9B54E';
                      e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.backgroundColor = getThemes?.color1 || '#1A2347';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  <div className="mb-2 flex justify-center">
                    <tab.icon className="w-8 h-8" style={{ color: getThemes?.txtColor || '#ffffff' }} />
                  </div>
                  <div className="text-sm font-medium" style={{ color: getThemes?.txtColor || '#ffffff' }}>
                    {tab.label}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Feedback Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#000000' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Brief description of your feedback..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:border-transparent transition-all duration-200"
                  style={{ 
                    '--tw-ring-color': getThemes?.color1 || '#1A2347'
                  } as React.CSSProperties}
                  required
                />
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#000000' }}>
                  Priority
                </label>
                <div className="relative" ref={priorityDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsPriorityOpen(!isPriorityOpen)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:border-transparent transition-all duration-200 text-left flex items-center justify-between"
                    style={{ 
                      '--tw-ring-color': getThemes?.color1 || '#1A2347'
                    } as React.CSSProperties}
                  >
                    <div className="flex items-center gap-3">
                      {formData.priority ? (
                        <>
                          {(() => {
                            const priority = priorities.find(pri => pri.value === formData.priority);
                            const IconComponent = priority?.icon || Circle;
                            return <IconComponent className="w-4 h-4" style={{ color: priority?.color || '#10B981' }} />;
                          })()}
                          <span>{priorities.find(pri => pri.value === formData.priority)?.label || 'Select priority...'}</span>
                        </>
                      ) : (
                        <span className="text-gray-500">Select priority...</span>
                      )}
                    </div>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {isPriorityOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-2xl max-h-60 overflow-y-auto backdrop-blur-sm" style={{ backgroundColor: '#ffffff', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
                      {priorities.map((priority) => {
                        const IconComponent = priority.icon;
                        return (
                          <button
                            key={priority.value}
                            type="button"
                            onClick={() => {
                              handleInputChange('priority', priority.value as 'low' | 'medium' | 'high');
                              setIsPriorityOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-left flex items-center gap-3 transition-colors duration-200 ${
                              formData.priority === priority.value 
                                ? 'bg-white text-black font-medium' 
                                : 'bg-white hover:bg-gray-50 text-gray-700'
                            }`}
                          >
                            <IconComponent className="w-4 h-4" style={{ color: priority.color }} />
                            <span>{priority.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#000000' }}>
                  Category
                </label>
                <div className="relative" ref={categoryDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:border-transparent transition-all duration-200 text-left flex items-center justify-between"
                    style={{ 
                      '--tw-ring-color': getThemes?.color1 || '#1A2347'
                    } as React.CSSProperties}
                  >
                    <div className="flex items-center gap-3">
                      {formData.category ? (
                        <>
                          {(() => {
                            const category = categories.find(cat => cat.value === formData.category);
                            const IconComponent = category?.icon || Squirrel;
                            return <IconComponent className="w-4 h-4" style={{ color: getThemes?.color1 || '#1A2347' }} />;
                          })()}
                          <span>{categories.find(cat => cat.value === formData.category)?.label || 'Select a category...'}</span>
                        </>
                      ) : (
                        <span className="text-gray-500">Select a category...</span>
                      )}
                    </div>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {isCategoryOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-2xl max-h-60 overflow-y-auto backdrop-blur-sm" style={{ backgroundColor: '#ffffff', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
                      {categories.map((category) => {
                        const IconComponent = category.icon;
                        return (
                          <button
                            key={category.value}
                            type="button"
                            onClick={() => {
                              handleInputChange('category', category.value);
                              setIsCategoryOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-left flex items-center gap-3 transition-colors duration-200 ${
                              formData.category === category.value 
                                ? 'bg-white text-black font-medium' 
                                : 'bg-white hover:bg-gray-50 text-gray-700'
                            }`}
                          >
                            <IconComponent className="w-4 h-4" style={{ color: getThemes?.color1 || '#1A2347' }} />
                            <span>{category.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#000000' }}>
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Please provide detailed information about your feedback..."
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:border-transparent transition-all duration-200 resize-none"
                  style={{ 
                    '--tw-ring-color': getThemes?.color1 || '#1A2347'
                  } as React.CSSProperties}
                  required
                />
              </div>

              {/* Bug Report Specific Fields */}
              {activeTab === 'bug-report' && (
                <>
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#000000' }}>
                      Steps to Reproduce
                    </label>
                    <textarea
                      value={formData.steps || ''}
                      onChange={(e) => handleInputChange('steps', e.target.value)}
                      placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:border-transparent transition-all duration-200 resize-none"
                      style={{ 
                        '--tw-ring-color': getThemes?.color1 || '#1A2347'
                      } as React.CSSProperties}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2" style={{ color: '#000000' }}>
                        Expected Behavior
                      </label>
                      <textarea
                        value={formData.expected || ''}
                        onChange={(e) => handleInputChange('expected', e.target.value)}
                        placeholder="What should have happened?"
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:border-transparent transition-all duration-200 resize-none"
                        style={{ 
                          '--tw-ring-color': getThemes?.color1 || '#1A2347'
                        } as React.CSSProperties}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2" style={{ color: '#000000' }}>
                        Actual Behavior
                      </label>
                      <textarea
                        value={formData.actual || ''}
                        onChange={(e) => handleInputChange('actual', e.target.value)}
                        placeholder="What actually happened?"
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:border-transparent transition-all duration-200 resize-none"
                        style={{ 
                          '--tw-ring-color': getThemes?.color1 || '#1A2347'
                        } as React.CSSProperties}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Submit Button */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting || !formData.title || !formData.description}
                  className="w-full py-4 px-6 border border-transparent text-lg font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                  style={{
                    backgroundColor: getThemes?.color1 || '#1A2347',
                    color: getThemes?.txtColor || '#ffffff'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting && formData.title && formData.description) {
                      e.currentTarget.style.backgroundColor = getThemes?.color2 || '#E9B54E';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSubmitting && formData.title && formData.description) {
                      e.currentTarget.style.backgroundColor = getThemes?.color1 || '#1A2347';
                    }
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                      </svg>
                      Submit Feedback
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Help Text */}
          <div className="mt-8 text-center">
            <p className="text-sm" style={{ color: '#000000' }}>
              <strong>Tip:</strong> The more details you provide, the better we can understand and address your feedback
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* Success Modal using Portal */}
    {showSuccessModal && ReactDOM.createPortal(
      <div 
        className="fixed inset-0 flex items-center justify-center z-[9999]"
        style={{ 
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          transition: 'all 0.3s ease',
          opacity: showSuccessModal ? 1 : 0,
          visibility: showSuccessModal ? 'visible' : 'hidden'
        }}
        onClick={closeModal}
      >
        <div 
          className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300"
          style={{
            backgroundColor: "white",
            transform: showSuccessModal ? 'scale(1)' : 'scale(0.95)',
            opacity: showSuccessModal ? 1 : 0,
            transition: 'transform 0.3s ease, opacity 0.3s ease'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
          
          {/* Modal Body */}
          <div className="p-8 text-center">
            {/* Success Icon */}
            <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: getThemes?.color2 || '#E9B54E' }}>
              <Waypoints className="w-10 h-10" style={{ color: getThemes?.txtColor || '#ffffff' }} />
            </div>
            
            {/* Title */}
            <h2 className="text-3xl font-bold mb-4" style={{ color: getThemes?.txtColor }}>
              Thank You!
            </h2>
            
            {/* Message */}
            <p className="text-lg mb-8 text-gray-700">
              Your feedback has been submitted successfully, thank you for your input.
            </p>
            
            {/* Action Buttons */}
            <div className="space-y-4">
              <button
                onClick={resetForm}
                className="w-full py-3 px-6 border border-transparent text-base font-medium rounded-lg transition-all duration-200"
                style={{
                  backgroundColor: getThemes?.color1 || '#1A2347',
                  color: getThemes?.txtColor || '#ffffff'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = getThemes?.color2 || '#E9B54E';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = getThemes?.color1 || '#1A2347';
                }}
              >
                Submit More Feedback
              </button>
            </div>
            
            {/* Footer Text */}
            <div className="mt-6 text-sm text-gray-500">
              We'll review your feedback and get back to you if more details are needed.
            </div>
          </div>
        </div>
      </div>,
      document.body
    )}
    </>
  );
};

export default Feedback;
